/**
 * 任务进度卡片组件
 *
 * 显示索引任务的完整进度信息
 */

import React from 'react';
import { Card, Space, Tag, Button, Divider, Typography, Progress } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
    PauseCircleOutlined,
    ReloadOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';

import type { IndexingTask } from '../../services/api';
import { ProgressBar, CompactProgressBar } from './ProgressBar';
import { formatDateTime } from '@/utils/date';

const { Text, Paragraph } = Typography;

export interface TaskProgressCardProps {
    /** 任务数据 */
    task: IndexingTask;
    /** 是否紧凑模式 */
    compact?: boolean;
    /** 取消任务回调 */
    onCancel?: (taskId: number) => void;
    /** 重试任务回调 */
    onRetry?: (taskId: number) => void;
    /** 是否显示操作按钮 */
    showActions?: boolean;
}

/**
 * 任务进度卡片
 */
export const TaskProgressCard: React.FC<TaskProgressCardProps> = ({
    task,
    compact = false,
    onCancel,
    onRetry,
    showActions = true,
}) => {
    // 获取状态标签
    const getStatusTag = () => {
        const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
            pending: { color: 'default', icon: <ClockCircleOutlined /> },
            processing: { color: 'processing', icon: <SyncOutlined spin /> },
            completed: { color: 'success', icon: <CheckCircleOutlined /> },
            failed: { color: 'error', icon: <CloseCircleOutlined /> },
            cancelled: { color: 'warning', icon: <PauseCircleOutlined /> },
        };

        const config = statusConfig[task.status] || statusConfig.pending;
        return (
            <Tag color={config.color} icon={config.icon}>
                {
                    {
                        pending: '等待中',
                        processing: '处理中',
                        completed: '已完成',
                        failed: '失败',
                        cancelled: '已取消',
                    }[task.status]
                }
            </Tag>
        );
    };

    // 获取阶段进度
    const getStageProgress = () => {
        const stages: Array<{ key: string; name: string; status: string; progress: number }> = [];

        // 文本提取阶段
        stages.push({
            key: 'extracting',
            name: '文本提取',
            status:
                !task.current_stage || task.current_stage === 'extracting'
                    ? task.status === 'processing'
                        ? 'processing'
                        : task.status === 'completed'
                            ? 'success'
                            : 'wait'
                    : task.current_stage === 'embedding' || task.current_stage === 'storing'
                        ? 'success'
                        : 'wait',
            progress:
                !task.current_stage || task.current_stage === 'extracting'
                    ? task.progress * 0.5
                    : task.current_stage === 'embedding' || task.current_stage === 'storing'
                        ? 100
                        : 0,
        });

        // 向量化阶段
        stages.push({
            key: 'embedding',
            name: '向量化',
            status:
                task.current_stage === 'embedding'
                    ? 'processing'
                    : task.current_stage === 'storing' || task.status === 'completed'
                        ? 'success'
                        : 'wait',
            progress:
                task.current_stage === 'embedding'
                    ? 20 + (task.progress - 20) * 2
                    : task.current_stage === 'storing' || task.status === 'completed'
                        ? 100
                        : 0,
        });

        // 存储入库阶段
        stages.push({
            key: 'storing',
            name: '存储入库',
            status:
                task.current_stage === 'storing'
                    ? 'processing'
                    : task.status === 'completed'
                        ? 'success'
                        : 'wait',
            progress:
                task.current_stage === 'storing'
                    ? 60 + (task.progress - 60) * 2.5
                    : task.status === 'completed'
                        ? 100
                        : 0,
        });

        return stages;
    };

    // 渲染操作按钮
    const renderActions = () => {
        if (!showActions) return null;

        const retryCount = task.retry_count || 0;
        const maxRetries = task.max_retries || 3;

        return (
            <Space>
                {task.status === 'processing' && (
                    <Button
                        size="small"
                        danger
                        onClick={() => onCancel?.(task.id)}
                        icon={<PauseCircleOutlined />}
                    >
                        取消任务
                    </Button>
                )}
                {task.status === 'failed' && retryCount < maxRetries && (
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => onRetry?.(task.id)}
                        icon={<ReloadOutlined />}
                    >
                        重试 ({retryCount}/{maxRetries})
                    </Button>
                )}
            </Space>
        );
    };

    // 紧凑模式
    if (compact) {
        return (
            <Card size="small" variant="borderless">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                        {getStatusTag()}
                        <Text type="secondary">进度：{task.progress || 0}%</Text>
                    </Space>
                    <CompactProgressBar
                        progress={task.progress || 0}
                        status={task.status}
                        stage={task.current_stage || ''}
                    />
                </Space>
            </Card>
        );
    }

    // 完整模式
    return (
        <Card
            title={
                <Space>
                    <Text strong>索引任务 #{task.id}</Text>
                    {getStatusTag()}
                </Space>
            }
            extra={renderActions()}
            size="small"
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* 主进度条 */}
                <ProgressBar
                    progress={task.progress}
                    stage={task.current_stage}
                    status={task.status}
                    processedChunks={task.processed_chunks}
                    totalChunks={task.total_chunks}
                    estimatedRemaining={task.estimated_remaining}
                    showDetail
                    size="large"
                />

                <Divider style={{ margin: '8px 0' }} />

                {/* 阶段详情 */}
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        处理阶段
                    </Text>
                    <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                        {getStageProgress().map((stage) => (
                            <Space key={stage.key} style={{ width: '100%' }}>
                                <Text style={{ width: 80 }}>{stage.name}</Text>
                                <Progress
                                    percent={stage.progress}
                                    status={stage.status as any}
                                    size="small"
                                    style={{ flex: 1 }}
                                    strokeColor={
                                        stage.status === 'success'
                                            ? '#52c41a'
                                            : stage.status === 'processing'
                                                ? '#1890ff'
                                                : '#d9d9d9'
                                    }
                                />
                            </Space>
                        ))}
                    </Space>
                </div>

                {/* 统计信息 */}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Space wrap>
                        <Tag color="blue">切片：{task.processed_chunks || 0}/{task.total_chunks || 0}</Tag>
                        <Tag color="green">切片大小：{task.chunk_size || '自动'}</Tag>
                        <Tag color="purple">重叠：{task.chunk_overlap || 50}</Tag>
                        <Tag color="orange">重试：{task.retry_count || 0}/{task.max_retries || 3}</Tag>
                    </Space>
                </Space>

                {/* 时间信息 */}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Paragraph style={{ fontSize: 12, margin: 0 }}>
                        <ClockCircleOutlined /> 创建时间：{formatDateTime(task.created_at)}
                        {task.started_at && (
                            <>
                                <br />
                                开始时间：{formatDateTime(task.started_at)}
                            </>
                        )}
                        {task.completed_at && (
                            <>
                                <br />
                                完成时间：{formatDateTime(task.completed_at)}
                            </>
                        )}
                    </Paragraph>
                </Space>

                {/* 错误信息 */}
                {task.error_message && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Paragraph
                            style={{
                                fontSize: 12,
                                color: '#ff4d4f',
                                backgroundColor: '#fff1f0',
                                padding: 8,
                                borderRadius: 4,
                                margin: 0,
                            }}
                        >
                            <CloseCircleOutlined /> 错误：{task.error_message}
                        </Paragraph>
                    </>
                )}
            </Space>
        </Card>
    );
};
